import { useState } from "react"
import { X } from "lucide-react"
import ReactCountryFlag from "react-country-flag"
import User from "../../../src/assets/images/user.svg"
import Identification from "../../assets/images/identification-card.svg"
import Mobile from "../../../src/assets/images/mobile.svg"
import Email from "../../../src/assets/images/envelope-simple.svg"
import BagIcon from "../../../src/assets/images/bag-simple.svg"
import BuildingIcon from '../../assets/images/buildings.svg'
import Linkedin from "../../../src/assets/images/linkedin.svg"
import Github from "../../../src/assets/images/github.svg"


// Helper function to parse education string
const parseEducation = (educationStr) => {
  if (!educationStr) return [];
  return educationStr.split(';')
    .filter(edu => edu.trim())
    .map(edu => {
      const [school, degree, years] = edu.split(',').map(s => s.trim());
      const [startYear, endYear] = years ? years.split('-').map(y => y.replace('/', '').trim()) : ['', ''];
      return {
        school,
        degree,
        start_date: startYear,
        end_date: endYear
      };
    });
};

// Country code mapping
const countryToCode = {
  "United States": "US",
  "Canada": "CA",
  "United Kingdom": "GB",
  "Australia": "AU",
  "Germany": "DE",
  "France": "FR",
  "India": "IN",
  "China": "CN",
  "Japan": "JP",
  "Brazil": "BR",
};

export default function TalentDetails({ onClose, talent, checkedResults, onCheck }) {
  const [activeTab, setActiveTab] = useState("experience")
  
  if (!talent) return null

  const parsedEducation = parseEducation(talent.education);
  const countryCode = countryToCode[talent.country?.[0]] || 'US';

  return (
    <div className="h-full overflow-auto">
      <div className="p-6">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <input 
              type="checkbox"
              className="h-4 w-4"
              checked={checkedResults?.includes(talent.id)}
              onChange={(e) => onCheck(talent.id, e)}
            />
            <h2 className="text-lg font-medium">Talent Details</h2>
          </div>
          <button onClick={onClose} className="p-1">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="flex items-start space-x-6 mb-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-2xl font-semibold text-gray-600 overflow-hidden">
            <img src={talent.profileImage || User} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-3xl font-medium mb-1">{`${talent.first_name} ${talent.last_name}`}</h3>
              <div className="flex space-x-2">
              {talent.linkedin&& <button 
              className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white"
              onClick={(e) => {
                if(talent.linkedin){
                  e.stopPropagation()
                  window.open(talent.linkedin, '_blank')
                }
                
              }}
            >
              <img src={Linkedin}/>
            </button>}
                {talent.github && (
                  <a 
                    href={talent.github} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-white hover:bg-gray-900 transition-colors"
                  >
                    <img src={Github} className="w-4 h-4"/>
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center text-base text-gray-600">
              <div className="w-[20px] h-[20px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center">
                <ReactCountryFlag
                  countryCode={countryCode}
                  svg
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <span className="font-normal text-sm ml-2">{`${talent.work_cities?.[0] || ''}, ${talent.state || ''}, ${talent.country?.[0] || ''}`}</span>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-8">
          <div className="flex items-center">
            <div className="flex space-x-1 w-40 text-xs text-gray-500">
              <img src={Identification}/>
              <span className="font-normal text-sm">Current Company</span>
            </div>
            <div className="flex-1 text-sm">{talent.employer}</div>
          </div>
          <div className="flex items-center">
            <div className="flex space-x-1 w-40 text-xs text-gray-500">
              <img src={BagIcon}/>
              <span className="font-normal text-sm">Job Title</span>
            </div>
            <div className="flex-1 text-sm">{talent.title}</div>
          </div>
          <div className="flex items-center">
            <div className="flex space-x-1 w-40 text-xs text-gray-500">
              <img src={Mobile}/>
              <span className="font-normal text-sm">Mobile</span>
            </div>
            <div className="flex-1 text-sm">{talent.mobile_phone || 'N/A'}</div>
          </div>
          <div className="flex items-center">
            <div className="flex space-x-1 w-40 text-xs text-gray-500">
              <img src={Email}/>
              <span className="font-normal text-sm">Work Email</span>
            </div>
            <div className="flex-1 text-sm">
              {talent.work_email ? (
                <a 
                  href={`mailto:${talent.work_email.split(',')[0]}`}
                  className="text-blue-600 hover:underline"
                >
                  {talent.work_email.split(',')[0]}
                </a>
              ) : 'N/A'}
            </div>
          </div>
          <div className="flex items-center">
            <div className="flex space-x-1 w-40 text-xs text-gray-500">
              <img src={Email}/>
              <span className="font-normal text-sm">Home Email</span>
            </div>
            <div className="flex-1 text-sm">
              {talent.home_email ? (
                <a 
                  href={`mailto:${talent.home_email}`}
                  className="text-blue-600 hover:underline"
                >
                  {talent.home_email}
                </a>
              ) : 'N/A'}
            </div>
          </div>
        </div>

        <div className="w-full">
          <div className="grid w-full grid-cols-3 mb-6 border-b">
            <button
              className={`py-2 text-sm font-medium ${activeTab === "experience" ? "border-b-2 border-[#8d50fe]" : ""}`}
              onClick={() => setActiveTab("experience")}
            >
              Experience
            </button>
            <button
              className={`py-2 text-sm font-medium ${activeTab === "education" ? "border-b-2 border-[#8d50fe]" : ""}`}
              onClick={() => setActiveTab("education")}
            >
              Education
            </button>
            <button
              className={`py-2 text-sm font-medium ${activeTab === "skills" ? "border-b-2 border-[#8d50fe]" : ""}`}
              onClick={() => setActiveTab("skills")}
            >
              Skills
            </button>
          </div>

          {activeTab === "experience" && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="mr-3 mt-1 w-[40px] h-[40px] flex items-center justify-center  rounded-full bg-[#ffffff] ">
                    <img src={BuildingIcon}/>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm font-medium">{talent.employer}</div>
                        <div className="text-sm text-gray-600">{talent.title}</div>
                      </div>
                      <div className="text-xs text-gray-500">Present</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "education" && (
            <div className="space-y-4">
              {parsedEducation.map((edu, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="mr-3 mt-1 w-[40px] h-[40px] flex items-center justify-center  rounded-full bg-[#ffffff] ">
                      <img src={BuildingIcon}/>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium">{edu.school}</div>
                          <div className="text-sm text-gray-600">{edu.degree}</div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {edu.start_date && edu.end_date ? `${edu.start_date} - ${edu.end_date}` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "skills" && (
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {talent.skills2?.map((skill, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

